import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Check, Info, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { Cell } from 'react-table';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { AppLayout, CustomPrimaryButton, DataTable, FundWalletModal } from '../../../../../components';
import { CreateBookingModel, CreatePassengerBooking } from '../../../../../models/request';
import { TransactionsService } from '../../../../../services';
import { ApplicationState } from '../../../../../store';
import { BookingsThunk } from '../../../../../store/bookings/bookings.thunk';
import { formatCurrency, formatDate, formatTime } from '../../../../../util/misc/formatters';
import { PresentationalFormRadio } from '../../../../../components/form';

import Card from '../../../../../assets/images/card.png';
import { CreateBookingInvoice } from './create-booking-invoice';
import { history } from '../../../../../util/history';

interface Props {
    passengerBookings: CreatePassengerBooking[];
    goBack: () => void;
    proceed: () => void;
    client: string;
    setBooking: (bookings: any) => void;
}

const InsufficientFundsWrapper = styled.div`
    width: 100%;
    font-size: 0.85rem;
    padding: 0.75em;
    color: #f4f4f4;
    background-color: rgb(247, 123, 114);
    font-weight: 500;
    display: flex;
    align-items: center;
    border-radius: 8px;
    justify-content: space-between;

    button {
        background-color: #f4f4f4;
        color: rgb(247, 123, 114);
        border: none;
        padding: 0.5em 1em;
        border-radius: 8px;
        font-weight: 700;
        transition: color 0.5s ease-in;
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &:hover {
            color: rgba(247, 123, 114, 0.863);
            background-color: #f4f4f4cc;
        }
    }
`;

export const CreateBookingVerificationPage: React.FC<Props> = ({
    passengerBookings,
    goBack,
    client,
    proceed,
    setBooking,
}) => {
    const [isFundingWallet, setFundingWallet] = useState(false);
    const [balance, setBalance] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const params: any = useParams();

    const { services } = useSelector((state: ApplicationState) => state);

    const getAdditionalServicePrice = (serviceId: number) => {
        let price = 0;
        for (let service of services.services) {
            if (service.id === serviceId) {
                price = service.price;
            }
        }

        return price;
    };

    const calculateAdditionalServicePrices = () => {
        let total = 0;
        for (let pass of passengerBookings) {
            total += getAdditionalServicePrice(pass.additionalServiceId);
        }
        return total;
    };

    const totalAdditionalService: number = calculateAdditionalServicePrices();

    const price = React.useMemo(
        () => passengerBookings.length * passengerBookings[0]?.Service.price + totalAdditionalService,
        [passengerBookings]
    );
    const { airports } = useSelector((state: ApplicationState) => state.airports);
    const dispatch = useDispatch();

    const handleCreateBooking = async () => {
        const bookingsMap: Record<string, CreateBookingModel> = {};

        passengerBookings.forEach((passengerBooking) => {
            const hash = CreatePassengerBooking.hashDetails(passengerBooking);

            if (hash in bookingsMap) {
                bookingsMap[hash]?.Passengers.push(passengerBooking.toPassenger());
                return;
            }

            const booking = passengerBooking.toBooking();
            booking.finalize();
            bookingsMap[hash] = booking;
        });

        let bookings = Object.values(bookingsMap);
        // bookings.clientId = client;
        const data: any = await dispatch(BookingsThunk.create(bookings, client));
        if (data['item2'][0]?.invoiceNumber !== null) {
            const returnBooking = { ...data['item1'][0], transaction: { ...data['item2'][0] } };
            await setBooking(returnBooking);
        }
    };

    const handledleConfirmBooking = async () => {
        await handleCreateBooking();
        await proceed();
    };

    useEffect(() => {
        TransactionsService.getWalletBalance(client)
            .then(({ data }) => {
                setBalance(data?.balance.balance ?? 0);
            })
            .catch((err) => console.log({ err }));
    }, []);

    return (
        <React.Fragment>
            {/* Booking Verification Page */}
            {balance < price && (
                <InsufficientFundsWrapper className="my-3">
                    {isFundingWallet && (
                        <FundWalletModal
                            onComplete={(value) => setBalance((prev) => prev + value)}
                            isOpen={isFundingWallet}
                            isCreatingBooking
                            setIsOpen={setFundingWallet}
                            client={client}
                        />
                    )}
                    <span>
                        You do not have enough balance to complete this process, Please Fund your wallet or use the Pay
                        with Card option below
                    </span>

                    <button onClick={() => setFundingWallet(true)}>Fund wallet</button>
                </InsufficientFundsWrapper>
            )}
            <AppLayout.Body>
                <AppLayout.TabHeader>
                    <div className="my-4 d-flex justify-content-between">
                        <span>Kindly review and confirm your Service booking details</span>
                        <b>{passengerBookings.length} Passenger(s)</b>
                    </div>
                </AppLayout.TabHeader>
                <div className="tab__content__row mb-5">
                    <DataTable
                        columns={[
                            {
                                Header: 'S/N',
                                Cell: (cell: Cell<CreatePassengerBooking>) => cell.row.index + 1,
                            },
                            {
                                Header: 'Name',
                                Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) =>
                                    passenger.FirstName + ' ' + passenger.LastName,
                            },
                            {
                                Header: 'Airport',
                                accessor: 'AirportId',
                                Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) =>
                                    find(airports, { id: passenger.AirportId })?.name,
                            },
                            {
                                Header: 'Airline',
                                Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) =>
                                    passenger.airline,
                            },
                            {
                                Header: 'Flight No.',
                                accessor: 'Flight_Number',
                            },
                            {
                                Header: 'Travel date',
                                accessor: 'Date_Of_Travel',
                                Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) =>
                                    formatDate(passenger.Date_Of_Travel),
                            },
                            {
                                Header: 'Flight time',
                                accessor: 'Date_Of_Arrival',
                                Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) =>
                                    formatTime(passenger.Date_Of_Arrival),
                            },
                            // {
                            //     Header: 'Wheel chair',
                            //     accessor: 'Wheel_Chair',
                            //     Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) =>
                            //         passenger.Wheel_Chair ? (
                            //             <Check size={14} color="var(--primary-color)" strokeWidth={2} />
                            //         ) : (
                            //             <X size={14} strokeWidth={2} />
                            //         ),
                            // },
                            {
                                Header: 'Special requirements',
                                accessor: 'special_Requirement' as any,
                                Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) => {
                                    return (
                                        <div>
                                            {passenger.Wheel_Chair ? (
                                                <span>Wheel chair</span>
                                            ) : !passenger.Special_Requirement ? (
                                                <X size={14} strokeWidth={2} />
                                            ) : (
                                                <span>{passenger.Special_Requirement}</span>
                                            )}
                                        </div>
                                    );
                                },
                            },
                            // {
                            //     Header: 'Addtional service',
                            //     accessor: 'additionalServiceId' as any,
                            //     Cell: ({ row: { original: passenger } }: Cell<CreatePassengerBooking>) =>
                            //         find(services.services, { id: passenger.additionalServiceId })?.name ?? '',
                            // },
                        ]}
                        data={passengerBookings}
                    />
                    <div className="d-flex mt-3 mb-5 justify-content-end">
                        <b>Total Price {formatCurrency(price)}</b>
                    </div>
                    <div className="d-flex mt-4 justify-content-end">
                        <div>
                            <CustomPrimaryButton className="inverted" onClick={goBack}>
                                <span>Edit Details</span>
                            </CustomPrimaryButton>
                            {balance >= price && (
                                <CustomPrimaryButton onClick={handleCreateBooking}>
                                    <span>Confirm</span>
                                </CustomPrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </AppLayout.Body>
            {balance < price && (
                <div className="mt-5">
                    <div>
                        <b>Alternative Method</b>
                    </div>
                    <div className="mt-3">
                        <div
                            style={{ maxWidth: '300px' }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <PresentationalFormRadio checked size="medium" />
                            </div>
                            <div className="d-flex align-items-center">
                                <img width={130} height={50} src={Card} />
                                <div className="pl-3">Pay with Card</div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5">
                        <CustomPrimaryButton onClick={handledleConfirmBooking}>
                            <span>Confirm</span>
                        </CustomPrimaryButton>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};
